import $ from "jquery";
import * as z from "zod/mini";

import * as blueslip from "./blueslip.ts";
import * as browser_history from "./browser_history.ts";
import * as channel from "./channel.ts";
import type {MessageId} from "./message_id.ts";
import * as breakpage_view from "./breakpage_view.ts";
import * as emoji from "./emoji.ts";
import type {LocalStorage} from "./localstorage.ts";
import {localstorage} from "./localstorage.ts";
import * as hash_util from "./hash_util.ts";
import * as message_helper from "./message_helper.ts";
import * as message_store from "./message_store.ts";
import type {Message} from "./message_store.ts";
import {raw_message_schema} from "./message_store.ts";
import type {LeftSidebarCategory} from "./types.ts";
import * as ui_util from "./ui_util.ts";

type BlockData = {
    blockId: string;
    categories: LeftSidebarCategory[];
    override: boolean;
};

type CategoryEntry = {
    key: string;
    category: LeftSidebarCategory;
    blockKey: string;
    override: boolean;
};

const categorySources = new Map<number, BlockData[]>();
const collapsedSections = new Set<string>();
let currentEntries: CategoryEntry[] = [];
let overrideSidebarActive = false;
const destroyedBlockKeys = new Set<string>();
const pendingSidebarFetches = new Set<number>();
let activeSidebarMessageId: number | undefined;
const SIDEBAR_LINK_REGEX = /##lsb-(\d+)/i;
const messageLinkContainerClass = "left-sidebar-custom-message-link-container";
const messageLinkClass = "left-sidebar-custom-message-link";

const fetch_message_response_schema = z.object({
    messages: z.array(raw_message_schema),
});

const destroyedBlockKeysStorageKey = "left-sidebar-custom-destroyed-blocks";
const destroyedBlockKeysSchema = z.array(z.string());
const destroyedBlockStorage: LocalStorage | undefined = localstorage.supported()
    ? localstorage()
    : undefined;

function persistDestroyedBlockKeys(): void {
    if (destroyedBlockStorage === undefined) {
        return;
    }

    if (destroyedBlockKeys.size === 0) {
        destroyedBlockStorage.remove(destroyedBlockKeysStorageKey);
        return;
    }

    destroyedBlockStorage.set(
        destroyedBlockKeysStorageKey,
        Array.from(destroyedBlockKeys),
    );
}

function loadPersistedDestroyedBlockKeys(): void {
    if (destroyedBlockStorage === undefined) {
        return;
    }

    const stored = destroyedBlockStorage.get(destroyedBlockKeysStorageKey);
    if (stored === undefined) {
        return;
    }

    const parsed = destroyedBlockKeysSchema.safeParse(stored);
    if (!parsed.success) {
        destroyedBlockStorage.remove(destroyedBlockKeysStorageKey);
        return;
    }

    for (const key of parsed.data) {
        destroyedBlockKeys.add(key);
    }
}

loadPersistedDestroyedBlockKeys();

function buildBlockKey(messageId: number, blockId: string): string {
    return `${messageId}:${blockId}`;
}

function buildEntries(): CategoryEntry[] {
    const overrideEntries: CategoryEntry[] = [];
    const normalEntries: CategoryEntry[] = [];
    const sortedSources = [...categorySources.entries()].sort(([a], [b]) => a - b);
    for (const [messageId, blocks] of sortedSources) {
        for (const block of blocks) {
            const blockKey = buildBlockKey(messageId, block.blockId);
            if (destroyedBlockKeys.has(blockKey)) {
                continue;
            }
            const target = block.override ? overrideEntries : normalEntries;
            block.categories.forEach((category, index) => {
                target.push({
                    key: `${messageId}:${block.blockId}:${index}`,
                    category,
                    blockKey,
                    override: block.override,
                });
            });
        }
    }
    if (overrideEntries.length > 0) {
        overrideSidebarActive = true;
        return overrideEntries;
    }
    overrideSidebarActive = false;
    return normalEntries;
}

function clearActiveSidebar(): void {
    if (categorySources.size === 0 && activeSidebarMessageId === undefined) {
        return;
    }

    categorySources.clear();
    activeSidebarMessageId = undefined;
    recomputeEntries();
}

function filterDestroyedBlocks(messageId: number, blocks: BlockData[]): BlockData[] {
    const filtered: BlockData[] = [];
    for (const block of blocks) {
        const blockKey = buildBlockKey(messageId, block.blockId);
        if (destroyedBlockKeys.has(blockKey)) {
            continue;
        }
        filtered.push(block);
    }
    return filtered;
}

function activateSidebarForMessage(message: Message): void {
    const categories = message.left_sidebar_categories;
    if (categories === undefined || categories.length === 0) {
        // Remove this message's blocks but keep others
        categorySources.delete(message.id);
        recomputeEntries();
        return;
    }

    const blocks = filterDestroyedBlocks(message.id, groupCategoriesByBlock(categories));
    if (blocks.length === 0) {
        // Remove this message's blocks but keep others
        categorySources.delete(message.id);
        recomputeEntries();
        return;
    }

    // Add/update this message's blocks without clearing others
    // This allows multiple non-OVERRIDE blocks to stack
    categorySources.set(message.id, blocks);
    activeSidebarMessageId = message.id;
    recomputeEntries();
    
    // Hide the left sidebar search when a leftsidebar is activated
    // This matches the behavior when clicking ##LSB- links within chat
    ui_util.disable_left_sidebar_search();
}

function updateOverrideMode(active: boolean): void {
    const $container = $("#left-sidebar-custom-navigation");
    // When OVERRIDE is active, hide ALL default sidebar content
    const $sidebarSearch = $("#left-sidebar-search");
    const $navArea = $("#left-sidebar-navigation-area");
    const $dmSection = $("#direct-messages-section-header");
    const $scrollContainer = $("#left_sidebar_scroll_container");
    const $hideMoreDM = $("#hide-more-direct-messages");
    
    if (active) {
        // Hide all default sidebar sections when OVERRIDE is active
        $sidebarSearch.addClass("left-sidebar-override-hidden");
        $navArea.addClass("left-sidebar-override-hidden");
        $dmSection.addClass("left-sidebar-override-hidden");
        $scrollContainer.addClass("left-sidebar-override-hidden");
        $hideMoreDM.addClass("left-sidebar-override-hidden");
        $container.addClass("left-sidebar-override");
    } else {
        // Restore all default sidebar sections
        $sidebarSearch.removeClass("left-sidebar-override-hidden");
        $navArea.removeClass("left-sidebar-override-hidden");
        $dmSection.removeClass("left-sidebar-override-hidden");
        $scrollContainer.removeClass("left-sidebar-override-hidden");
        $hideMoreDM.removeClass("left-sidebar-override-hidden");
        $container.removeClass("left-sidebar-override");
    }
}

function recomputeEntries(): void {
    currentEntries = buildEntries();
    const validKeys = new Set(currentEntries.map((entry) => entry.key));
    for (const key of [...collapsedSections]) {
        if (!validKeys.has(key)) {
            collapsedSections.delete(key);
        }
    }
    renderCategories();
}

function renderIcon(icon: LeftSidebarCategory["links"][number]["icon"]): JQuery {
    if (icon.type === "zulip_icon") {
        return $("<i>")
            .addClass(`zulip-icon ${icon.name}`)
            .attr("aria-hidden", "true");
    }

    try {
        const details = emoji.get_emoji_details_by_name(icon.name);
        if (details.reaction_type === "unicode_emoji") {
            const codepoints = details.emoji_code.split("-").map((code) => Number.parseInt(code, 16));
            const glyph = String.fromCodePoint(...codepoints);
            return $("<span>")
                .addClass("left-sidebar-custom-emoji")
                .text(glyph)
                .attr("role", "img")
                .attr("aria-label", icon.name.replaceAll("_", " "));
        }

        const img = $("<img>")
            .addClass("left-sidebar-custom-emoji")
            .attr("alt", `:${icon.name}:`)
            .attr("title", icon.name.replaceAll("_", " "));
        if (details.url !== undefined) {
            img.attr("src", details.url);
        }
        if (details.still_url !== undefined && details.still_url !== null) {
            img.attr("data-still-url", details.still_url);
        }
        img.attr("data-emoji-code", details.emoji_code);
        img.attr("data-emoji-name", details.emoji_name);
        return img;
    } catch {
        return $("<span>").text(`:${icon.name}:`).addClass("left-sidebar-custom-emoji");
    }
}

function renderCategories(): void {
    const $container = $("#left-sidebar-custom-navigation");
    if ($container.length === 0) {
        return;
    }

    $container.empty();
    if (currentEntries.length === 0) {
        updateOverrideMode(false);
        $container.addClass("hidden");
        // Re-enable the left sidebar search when no leftsidebar is active
        ui_util.enable_left_sidebar_search();
        return;
    }
    updateOverrideMode(overrideSidebarActive);
    $container.removeClass("hidden");

    currentEntries.forEach((entry) => {
        const {category, key, blockKey} = entry;
        const listId = `left-sidebar-custom-links-${key.replaceAll(":", "-")}`;
        const $section = $("<div>")
            .addClass("left-sidebar-custom-section")
            .attr("data-key", key)
            .attr("data-block-key", blockKey);
        const isCollapsed = collapsedSections.has(key);
        if (isCollapsed) {
            $section.addClass("collapsed");
        }

        const $header = $("<div>").addClass("left-sidebar-custom-header");
        const $toggleButton = $("<button>")
            .addClass("left-sidebar-custom-toggle sidebar-heading-icon")
            .attr("type", "button")
            .attr("aria-controls", listId)
            .attr("aria-expanded", (!isCollapsed).toString());
        const $toggleIcon = $("<i>")
            .addClass("zulip-icon zulip-icon-heading-triangle-right rotate-icon-down")
            .attr("aria-hidden", "true");
        if (isCollapsed) {
            $toggleIcon.removeClass("rotate-icon-down").addClass("rotate-icon-right");
        }
        $toggleButton.append($toggleIcon);
        const $title = $("<h4>").addClass("left-sidebar-title").text(category.title);
        $header.append($toggleButton, $title);

        const $list = $("<ul>")
            .addClass("left-sidebar-custom-links")
            .attr("id", listId);
        for (const link of category.links) {
            const $item = $("<li>").addClass("left-sidebar-custom-item");
            const $anchor = $("<a>")
                .addClass("left-sidebar-custom-link left-sidebar-navigation-label-container")
                .attr("href", link.url);

            const $iconWrapper = $("<span>")
                .addClass("filter-icon left-sidebar-custom-icon");
            $iconWrapper.append(renderIcon(link.icon));
            const $label = $("<span>")
                .addClass("left-sidebar-navigation-label left-sidebar-custom-text")
                .text(link.text);
            $anchor.append($iconWrapper, $label);
            $item.append($anchor);
            $list.append($item);
        }

        $section.append($header, $list);
        $container.append($section);
    });
}

function isLocalLink(href: string): boolean {
    try {
        const url = new URL(href, window.location.origin);
        if (href.startsWith("//")) {
            return false;
        }
        return url.origin === window.location.origin;
    } catch {
        return false;
    }
}

function navigateLocally(href: string): void {
    if (href.startsWith("#") || href.startsWith("/#")) {
        browser_history.go_to_location(href);
        return;
    }

    window.location.assign(href);
}

function parseBlockIndex(blockId: string): number {
    const [prefix] = blockId.split("-");
    const value = Number.parseInt(prefix ?? "", 10);
    if (Number.isNaN(value)) {
        return 0;
    }
    return value;
}

function groupCategoriesByBlock(categories: LeftSidebarCategory[]): BlockData[] {
    const blocks = new Map<string, BlockData>();
    for (const category of categories) {
        const blockId = category.block_id;
        let block = blocks.get(blockId);
        if (block === undefined) {
            block = {
                blockId,
                categories: [],
                override: category.override,
            };
            blocks.set(blockId, block);
        }
        block.override = block.override || category.override;
        block.categories.push(category);
    }
    const grouped = [...blocks.values()];
    grouped.sort((a, b) => parseBlockIndex(a.blockId) - parseBlockIndex(b.blockId));
    return grouped;
}

function buildMessageSidebarLinkHref(message: Message): string {
    const baseUrl = message.url ?? hash_util.by_conversation_and_time_url(message);
    return `${baseUrl}##LSB-${message.id}`;
}

function removeMessageSidebarLink($content: JQuery, messageId: number): void {
    $content
        .find(`.${messageLinkContainerClass}[data-sidebar-message-id='${messageId}']`)
        .remove();
}

function renderMessageSidebarLink($content: JQuery, message: Message): void {
    const selector = `.${messageLinkContainerClass}[data-sidebar-message-id='${message.id}']`;
    const href = buildMessageSidebarLinkHref(message);
    const $existing = $content.find(selector);
    if ($existing.length > 0) {
        $existing
            .find(`.${messageLinkClass}`)
            .attr("href", href)
            .text(`##LSB-${message.id}`);
        return;
    }

    const $container = $("<p>")
        .addClass(messageLinkContainerClass)
        .attr("data-sidebar-message-id", message.id.toString());
    const $anchor = $("<a>")
        .addClass(messageLinkClass)
        .attr("href", href)
        .attr("data-sidebar-message-id", message.id.toString())
        .text(`##LSB-${message.id}`);
    $container.append($anchor);
    $content.append($container);
}

function reviveMessageBlocks(messageId: number): boolean {
    let modified = false;
    for (const key of [...destroyedBlockKeys]) {
        if (key.startsWith(`${messageId}:`)) {
            destroyedBlockKeys.delete(key);
            modified = true;
        }
    }
    if (modified) {
        persistDestroyedBlockKeys();
    }
    return modified;
}

function extractSidebarMessageIdFromString(value: string): number | undefined {
    const match = value.match(SIDEBAR_LINK_REGEX);
    if (match === null) {
        return undefined;
    }
    const messageId = Number.parseInt(match[1]!, 10);
    if (Number.isNaN(messageId)) {
        return undefined;
    }
    return messageId;
}

function cleanSidebarHash(hash: string): void {
    const index = hash.toLowerCase().indexOf("##lsb-");
    if (index === -1) {
        return;
    }
    const baseHash = hash.slice(0, index);
    const baseUrl = window.location.href.split("#")[0] + baseHash;
    try {
        window.history.replaceState(window.history.state, "", baseUrl);
    } catch (error) {
        blueslip.warn("Unable to clean sidebar hash", {hash, error});
    }
}

$(document).on("click", ".left-sidebar-custom-toggle", function (this: HTMLElement) {
    const $section = $(this).closest(".left-sidebar-custom-section");
    $section.toggleClass("collapsed");
    const collapsed = $section.hasClass("collapsed");
    $(this)
        .attr("aria-expanded", (!collapsed).toString())
        .find(".zulip-icon")
        .toggleClass("rotate-icon-down", !collapsed)
        .toggleClass("rotate-icon-right", collapsed);
    const key = $section.data("key");
    if (typeof key === "string") {
        if (collapsed) {
            collapsedSections.add(key);
        } else {
            collapsedSections.delete(key);
        }
    }
});

$(document).on("click", "a.left-sidebar-custom-link", function (this: HTMLAnchorElement, event) {
    const href = this.getAttribute("href");
    if (href === null) {
        return;
    }

    if (href === "#DESTROY") {
        event.preventDefault();
        const blockKey = $(this).closest(".left-sidebar-custom-section").attr("data-block-key");
        if (typeof blockKey === "string") {
            destroyBlock(blockKey);
        }
        return;
    }

    if (isLocalLink(href)) {
        event.preventDefault();
        breakpage_view.force_chat_view();
        navigateLocally(href);
        return;
    }

    event.preventDefault();
    const message =
        breakpage_view.get_current_message() ?? breakpage_view.get_suppressed_message();
    if (message !== undefined) {
        breakpage_view.show_external_iframe(message, href);
        return;
    }
    breakpage_view.show_external_iframe(undefined, href);
});

export function update_message_categories(message: Message): void {
    const messageId = message.id;
    const categories = message.left_sidebar_categories;
    let destroyedKeysModified = false;
    let shouldRecompute = false;
    if (categories !== undefined && categories.length > 0) {
        if (messageId !== activeSidebarMessageId) {
            return;
        }
        const filteredBlocks = filterDestroyedBlocks(
            messageId,
            groupCategoriesByBlock(categories),
        );
        if (filteredBlocks.length > 0) {
            categorySources.set(messageId, filteredBlocks);
        } else {
            categorySources.delete(messageId);
            activeSidebarMessageId = undefined;
        }
        shouldRecompute = true;
    } else {
        if (categorySources.delete(messageId)) {
            shouldRecompute = true;
        }
        if (activeSidebarMessageId === messageId) {
            activeSidebarMessageId = undefined;
            shouldRecompute = true;
        }
        for (const key of [...destroyedBlockKeys]) {
            if (key.startsWith(`${messageId}:`)) {
                destroyedBlockKeys.delete(key);
                destroyedKeysModified = true;
            }
        }
    }
    if (shouldRecompute) {
        recomputeEntries();
    }
    if (destroyedKeysModified) {
        persistDestroyedBlockKeys();
    }
}

export function remove_message(message_id: number): void {
    const blocks = categorySources.get(message_id);
    let destroyedKeysModified = false;
    if (blocks !== undefined) {
        categorySources.delete(message_id);
        for (const block of blocks) {
            const blockKey = buildBlockKey(message_id, block.blockId);
            if (destroyedBlockKeys.delete(blockKey)) {
                destroyedKeysModified = true;
            }

        }
        if (activeSidebarMessageId === message_id) {
            activeSidebarMessageId = undefined;
        }
        recomputeEntries();
        if (destroyedKeysModified) {
            persistDestroyedBlockKeys();
        }
        return;
    }

    for (const key of [...destroyedBlockKeys]) {
        if (key.startsWith(`${message_id}:`)) {
            destroyedBlockKeys.delete(key);
            destroyedKeysModified = true;
        }
    }
    if (destroyedKeysModified) {
        recomputeEntries();
        persistDestroyedBlockKeys();
    }
}

export function invalidate_messages(message_ids: Set<MessageId>): void {
    for (const message_id of categorySources.keys()) {
        if (message_ids.has(message_id)) {
            categorySources.delete(message_id);
        }
    }
    if (activeSidebarMessageId !== undefined && message_ids.has(activeSidebarMessageId)) {
        activeSidebarMessageId = undefined;
    }
    // An access change is not a deletion or a request to forget preferences.
    recomputeEntries();
}

function destroyBlock(blockKey: string): void {
    if (destroyedBlockKeys.has(blockKey)) {
        return;
    }
    destroyedBlockKeys.add(blockKey);
    persistDestroyedBlockKeys();

    const separatorIndex = blockKey.indexOf(":");
    const messagePart = separatorIndex === -1 ? "" : blockKey.slice(0, separatorIndex);
    const messageId = Number.parseInt(messagePart, 10);
    if (Number.isNaN(messageId)) {
        recomputeEntries();
        return;
    }

    const blockId = separatorIndex === -1 ? "" : blockKey.slice(separatorIndex + 1);
    const blocks = categorySources.get(messageId);
    if (blocks === undefined) {
        recomputeEntries();
        return;
    }

    const remaining = blocks.filter((block) => block.blockId !== blockId);
    if (remaining.length > 0) {
        categorySources.set(messageId, remaining);
    } else {
        categorySources.delete(messageId);
        if (activeSidebarMessageId === messageId) {
            activeSidebarMessageId = undefined;
        }
    }
    recomputeEntries();
}

function showSidebarForMessageId(messageId: number): void {
    reviveMessageBlocks(messageId);
    const message = message_store.get(messageId);
    if (message !== undefined) {
        activateSidebarForMessage(message);
        return;
    }

    if (pendingSidebarFetches.has(messageId)) {
        return;
    }
    pendingSidebarFetches.add(messageId);
    channel.get({
        url: "/json/messages",
        data: {
            allow_empty_topic_name: true,
            message_ids: JSON.stringify([messageId]),
        },
        success(raw_data) {
            pendingSidebarFetches.delete(messageId);
            let parsed;
            try {
                parsed = fetch_message_response_schema.parse(raw_data);
            } catch (error) {
                blueslip.warn("Failed to parse sidebar message response", {
                    message_id: messageId,
                    error,
                });
                return;
            }
            if (parsed.messages.length === 0) {
                blueslip.warn("Sidebar message response did not include any messages", {
                    message_id: messageId,
                });
                return;
            }
            const processed = message_helper.process_new_server_message(parsed.messages[0]!);
            reviveMessageBlocks(messageId);
            activateSidebarForMessage(processed);
        },
        error(xhr) {
            pendingSidebarFetches.delete(messageId);
            blueslip.warn("Unable to fetch sidebar message", {
                message_id: messageId,
                status: xhr?.status,
            });
        },
    });
}

function handleSidebarHashFromLocation(): void {
    const hash = window.location.hash;
    const messageId = extractSidebarMessageIdFromString(hash);
    if (messageId === undefined) {
        return;
    }
    showSidebarForMessageId(messageId);
    cleanSidebarHash(hash);
}

export function destroy_all(): void {
    if (currentEntries.length === 0) {
        return;
    }

    const blockKeys = new Set(currentEntries.map((entry) => entry.blockKey));
    for (const blockKey of blockKeys) {
        destroyBlock(blockKey);
    }
    activeSidebarMessageId = undefined;
}

export function ensure_message_has_sidebar_link(
    $content: JQuery,
    message: Message,
): void {
    const categories = message.left_sidebar_categories;
    const hasCategories = categories !== undefined && categories.length > 0;

    if (!hasCategories) {
        removeMessageSidebarLink($content, message.id);
        if (activeSidebarMessageId === message.id) {
            update_message_categories(message);
        }
        return;
    }

    renderMessageSidebarLink($content, message);
    if (activeSidebarMessageId === message.id) {
        update_message_categories(message);
    }
}

$(document).on(
    "click",
    "a[href*='##LSB-'], a[href*='##lsb-']",
    function (this: HTMLAnchorElement, event) {
        if (event.isDefaultPrevented()) {
            return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        if (event.which !== 1) {
            return;
        }
        const href = this.getAttribute("href");
        if (href === null) {
            return;
        }
        let url: URL | undefined;
        try {
            url = new URL(href, window.location.href);
        } catch {
            url = undefined;
        }
        if (url !== undefined && url.origin !== window.location.origin) {
            return;
        }
        const rawHash = url?.hash ?? href;
        const target = extractSidebarMessageIdFromString(rawHash);
        if (target === undefined) {
            return;
        }
        event.preventDefault();
        const baseHash = rawHash.replace(SIDEBAR_LINK_REGEX, "");
        breakpage_view.force_chat_view();
        navigateLocally(baseHash === "" ? "#" : baseHash);
        showSidebarForMessageId(target);
    },
);

$(window).on("hashchange", () => {
    handleSidebarHashFromLocation();
});

handleSidebarHashFromLocation();
